/**
 * useAccountSettingsHandlers Hook
 * 
 * Handles all account settings operations (groups, details, accounts).
 * This is a large hook due to the complexity of AccountSettings.
 */

import React from "react";
import { auth, db } from "../../../firebase";
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  Timestamp,
  deleteField,
} from "firebase/firestore";
import { Account, AccountGroupDetailField, AccountDetailValue } from "../../../types";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { isDemoMode } from "../../../utils/demoMode";
import { DemoModeMessage } from "../../../utils/demoModeMessage";
import { AccountGroup, UseAccountSettingsUIStateReturn } from "./useAccountSettingsUIState";
import { UseAccountSettingsHelpersReturn } from "./useAccountSettingsHelpers";

export interface UseAccountSettingsHandlersParams {
  workspaceId: string;
  groups: AccountGroup[];
  setGroups: React.Dispatch<React.SetStateAction<AccountGroup[]>>;
  accounts: Account[];
  setAccounts: React.Dispatch<React.SetStateAction<Account[]>>;
  defaultCurrency: string;
  helpers: UseAccountSettingsHelpersReturn;
  uiState: UseAccountSettingsUIStateReturn;
}

export interface UseAccountSettingsHandlersReturn {
  // Group handlers
  handleAddClick: () => void;
  handleAddGroup: () => Promise<void>;
  handleEditClick: (group: AccountGroup) => void;
  handleUpdateGroup: () => Promise<void>;
  handleDeleteClick: (groupName: string) => void;
  handleDeleteConfirm: () => Promise<void>;
  handleDragEnd: (event: DragEndEvent) => Promise<void>;
  handleGroupMenuOpen: (event: React.MouseEvent<HTMLElement>, group: AccountGroup) => void;
  handleGroupMenuClose: () => void;
  handleEditGroupFromMenu: () => void;
  handleManageDetailsFromMenu: () => void;
  handleDeleteGroupFromMenu: () => void;
  
  // Detail handlers
  handleSaveDetails: () => Promise<void>;
  handleAddDetailField: () => void;
  handleRemoveDetailField: (index: number) => void;
  handleEditDetailField: (index: number) => void;
  handleCancelEditDetail: () => void;
  handleDetailDragEnd: (event: DragEndEvent) => void;
  hasEditedDetailChanged: () => boolean;
  
  // Account handlers
  handleCreateAccountClick: () => void;
  handleEditAccount: () => void;
  handleDuplicateAccount: () => void;
  handleDeleteAccountClick: () => void;
  handleDeleteAccountConfirm: () => Promise<void>;
  handleAccountSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  handleAccountDragEnd: (event: DragEndEvent, groupName: string) => Promise<void>;
  handleAccountMenuOpen: (event: React.MouseEvent<HTMLElement>, account: Account) => void;
  handleAccountMenuClose: () => void;
  handleDetailValueChange: (detailName: string, value: string | number) => void;
  toggleAccountDetails: (accountId: string) => void;
}

/**
 * Hook for account settings operation handlers
 */
export const useAccountSettingsHandlers = (params: UseAccountSettingsHandlersParams): UseAccountSettingsHandlersReturn => {
  const {
    workspaceId,
    groups,
    setGroups,
    accounts,
    setAccounts,
    defaultCurrency,
    helpers,
    uiState,
  } = params;

  // Group handlers
  const handleAddClick = (): void => {
    uiState.setNewGroupName("");
    uiState.setNewGroupDescription("");
    uiState.setGroupNameError("");
    uiState.setIsAddingGroup(false);
    uiState.setAddDialogOpen(true);
  };

  const handleAddGroup = async (): Promise<void> => {
    // In demo mode, groups cannot be added
    if (isDemoMode()) {
      uiState.setSnackbarElement(
        <DemoModeMessage message="Demo mode: Account groups cannot be added. Please log in to add account groups." />
      );
      uiState.setSnackbarOpen(true);
      uiState.setAddDialogOpen(false);
      uiState.setNewGroupName("");
      uiState.setNewGroupDescription("");
      uiState.setGroupNameError("");
      return;
    }

    if (!workspaceId || !auth.currentUser || !uiState.newGroupName.trim() || uiState.isAddingGroup || uiState.groupNameError) return;

    const groupName = uiState.newGroupName.trim();

    // Validate group name (Firestore document ID restrictions)
    if (groupName.includes("/")) {
      alert("Group name cannot contain forward slashes (/)");
      return;
    }

    if (groupName.length === 0) {
      alert("Group name cannot be empty");
      return;
    }

    uiState.setIsAddingGroup(true);
    uiState.setGroupNameError("");
    try {
      const maxOrder = groups.length > 0 ? Math.max(...groups.map((g) => g.order)) : -1;
      const groupRef = doc(db, "workspaces", workspaceId, "accountGroups", groupName);
      
      const groupData: {
        name: string;
        order: number;
        createdAt: any;
        description?: string;
      } = {
        name: groupName,
        order: maxOrder + 1,
        createdAt: serverTimestamp(),
      };
      
      const trimmedDescription = uiState.newGroupDescription.trim();
      if (trimmedDescription) {
        groupData.description = trimmedDescription;
      }
      
      await setDoc(groupRef, groupData);

      uiState.setAddDialogOpen(false);
      uiState.setNewGroupName("");
      uiState.setNewGroupDescription("");
      uiState.setGroupNameError("");
    } catch (error: any) {
      console.error("Error adding group:", error);
      const errorMessage = error?.message || "Failed to add group";
      if (errorMessage.includes("permission") || errorMessage.includes("Permission")) {
        alert("Permission denied. Please check your Firestore security rules.");
      } else if (errorMessage.includes("already exists") || errorMessage.includes("already exists")) {
        alert(`A group named "${groupName}" already exists. Please choose a different name.`);
      } else {
        alert(`Failed to add group: ${errorMessage}`);
      }
    } finally {
      uiState.setIsAddingGroup(false);
    }
  };

  const handleEditClick = (group: AccountGroup): void => {
    uiState.setEditingGroup(group);
    uiState.setEditGroupName(group.name);
    uiState.setEditGroupDescription(group.description || "");
    uiState.setEditDialogOpen(true);
  };

  const handleUpdateGroup = async (): Promise<void> => {
    // In demo mode, groups cannot be edited
    if (isDemoMode()) {
      uiState.setSnackbarElement(
        <DemoModeMessage message="Demo mode: Account groups cannot be edited. Please log in to edit account groups." />
      );
      uiState.setSnackbarOpen(true);
      uiState.setEditDialogOpen(false);
      uiState.setEditingGroup(null);
      uiState.setEditGroupName("");
      uiState.setEditGroupDescription("");
      return;
    }

    if (!workspaceId || !auth.currentUser || !uiState.editingGroup || !uiState.editGroupName.trim()) return;

    const newName = uiState.editGroupName.trim();

    // Check if new name conflicts with existing group (excluding current)
    if (groups.some((g) => g.id !== uiState.editingGroup!.id && g.name.toLowerCase() === newName.toLowerCase())) {
      alert("A group with this name already exists");
      return;
    }

    try {
      // If name changed, we need to update all accounts using this group
      if (newName !== uiState.editingGroup.name) {
        const accountsUsingGroup = helpers.getAccountsUsingGroup(uiState.editingGroup.name);
        
        if (accountsUsingGroup.length > 0) {
          const batch = writeBatch(db);
          
          // Update accounts
          accountsUsingGroup.forEach((acc) => {
            const accountRef = doc(db, "workspaces", workspaceId, "accounts", acc.id);
            batch.update(accountRef, { group: newName });
          });

          // Delete old group
          const oldGroupRef = doc(db, "workspaces", workspaceId, "accountGroups", uiState.editingGroup.name);
          batch.delete(oldGroupRef);

          // Create new group
          const newGroupRef = doc(db, "workspaces", workspaceId, "accountGroups", newName);
          const updateData: {
            name: string;
            order: number;
            createdAt: Timestamp | Date;
            description?: string;
          } = {
            name: newName,
            order: uiState.editingGroup.order,
            createdAt: uiState.editingGroup.createdAt,
          };
          const trimmedDescription = uiState.editGroupDescription.trim();
          if (trimmedDescription) {
            updateData.description = trimmedDescription;
          }
          batch.set(newGroupRef, updateData);

          await batch.commit();
        } else {
          // No accounts using it, just rename the group document
          const oldGroupRef = doc(db, "workspaces", workspaceId, "accountGroups", uiState.editingGroup.name);
          const newGroupRef = doc(db, "workspaces", workspaceId, "accountGroups", newName);
          
          const batch = writeBatch(db);
          batch.delete(oldGroupRef);
          const updateData: {
            name: string;
            order: number;
            createdAt: Timestamp | Date;
            description?: string;
          } = {
            name: newName,
            order: uiState.editingGroup.order,
            createdAt: uiState.editingGroup.createdAt,
          };
          const trimmedDescription = uiState.editGroupDescription.trim();
          if (trimmedDescription) {
            updateData.description = trimmedDescription;
          }
          batch.set(newGroupRef, updateData);
          await batch.commit();
        }
      } else {
        // Name didn't change, just update the group document
        const groupRef = doc(db, "workspaces", workspaceId, "accountGroups", uiState.editingGroup.name);
        const trimmedDescription = uiState.editGroupDescription.trim();
        const updateData: { description?: string } = {};
        if (trimmedDescription) {
          updateData.description = trimmedDescription;
        } else {
          updateData.description = "";
        }
        await updateDoc(groupRef, updateData);
      }

      uiState.setEditDialogOpen(false);
      uiState.setEditingGroup(null);
      uiState.setEditGroupName("");
      uiState.setEditGroupDescription("");
    } catch (error) {
      console.error("Error updating group:", error);
      alert("Failed to update group");
    }
  };

  const handleDeleteClick = (groupName: string): void => {
    if (helpers.isGroupInUse(groupName)) {
      return;
    }
    uiState.setGroupToDelete(groupName);
    uiState.setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    // In demo mode, groups cannot be deleted
    if (isDemoMode()) {
      uiState.setSnackbarElement(
        <DemoModeMessage message="Demo mode: Account groups cannot be deleted. Please log in to delete account groups." />
      );
      uiState.setSnackbarOpen(true);
      uiState.setDeleteDialogOpen(false);
      uiState.setGroupToDelete("");
      return;
    }

    if (!workspaceId || !auth.currentUser || !uiState.groupToDelete) return;

    try {
      await deleteDoc(doc(db, "workspaces", workspaceId, "accountGroups", uiState.groupToDelete));
      uiState.setDeleteDialogOpen(false);
      uiState.setGroupToDelete("");
    } catch (error) {
      console.error("Error deleting group:", error);
      alert("Failed to delete group");
    }
  };

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    // In demo mode, group order cannot be changed
    if (isDemoMode()) {
      uiState.setSnackbarElement(
        <DemoModeMessage message="Demo mode: Group order cannot be changed. Please log in to reorder groups." />
      );
      uiState.setSnackbarOpen(true);
      return;
    }

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = groups.findIndex((g) => g.id === active.id);
    const newIndex = groups.findIndex((g) => g.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state immediately for better UX
    const newGroups = arrayMove(groups, oldIndex, newIndex);
    setGroups(newGroups);

    // Update order in Firestore
    try {
      const batch = writeBatch(db);
      newGroups.forEach((group, index) => {
        const groupRef = doc(db, "workspaces", workspaceId, "accountGroups", group.id);
        batch.update(groupRef, { order: index });
      });
      await batch.commit();
    } catch (error) {
      console.error("Error updating group order:", error);
      setGroups(groups);
      alert("Failed to update group order");
    }
  };

  const handleGroupMenuOpen = (event: React.MouseEvent<HTMLElement>, group: AccountGroup): void => {
    uiState.setGroupMenuAnchor(event.currentTarget);
    uiState.setSelectedGroup(group);
    uiState.setSelectedGroupInUse(helpers.isGroupInUse(group.name));
  };

  const handleGroupMenuClose = (): void => {
    uiState.setGroupMenuAnchor(null);
    uiState.setSelectedGroup(null);
    uiState.setSelectedGroupInUse(false);
  };

  const handleEditGroupFromMenu = (): void => {
    if (!uiState.selectedGroup) return;
    handleEditClick(uiState.selectedGroup);
    handleGroupMenuClose();
  };

  const handleManageDetailsFromMenu = (): void => {
    if (!uiState.selectedGroup) return;
    uiState.setEditingGroupName(uiState.selectedGroup.name);
    uiState.setEditingGroupDetails(uiState.selectedGroup.details ? [...uiState.selectedGroup.details] : []);
    uiState.setOriginalGroupDetails(uiState.selectedGroup.details ? [...uiState.selectedGroup.details] : []);
    uiState.setDetailsDialogOpen(true);
    handleGroupMenuClose();
  };

  const handleDeleteGroupFromMenu = (): void => {
    if (!uiState.selectedGroup) return;
    if (helpers.isGroupInUse(uiState.selectedGroup.name)) {
      alert(`Cannot delete: This group is used by ${helpers.getAccountsUsingGroup(uiState.selectedGroup.name).length} account(s)`);
      handleGroupMenuClose();
      return;
    }
    uiState.setGroupToDelete(uiState.selectedGroup.name);
    handleGroupMenuClose();
    uiState.setDeleteDialogOpen(true);
  };

  // Detail handlers
  const handleSaveDetails = async (): Promise<void> => {
    // In demo mode, group details cannot be saved
    if (isDemoMode()) {
      uiState.setSnackbarElement(
        <DemoModeMessage message="Demo mode: Group details cannot be saved. Please log in to manage group details." />
      );
      uiState.setSnackbarOpen(true);
      uiState.setDetailsDialogOpen(false);
      uiState.setEditingGroupDetails([]);
      uiState.setOriginalGroupDetails([]);
      uiState.setEditingGroupName("");
      uiState.setNewDetailName("");
      uiState.setNewDetailType("");
      uiState.setNewDetailDescription("");
      uiState.setNewListItems([]);
      uiState.setDetailNameError("");
      uiState.setEditingDetailIndex(null);
      uiState.setEditingOriginalDetail(null);
      return;
    }

    if (!uiState.editingGroupName || !workspaceId || !auth.currentUser) {
      console.error("Missing required data:", { editingGroupName: uiState.editingGroupName, workspaceId, currentUser: auth.currentUser });
      alert("Cannot save: Missing required information");
      return;
    }

    try {
      const groupRef = doc(db, "workspaces", workspaceId, "accountGroups", uiState.editingGroupName);
      const cleanedDetails = uiState.editingGroupDetails.map((detail) => ({
        name: detail.name.trim(),
        type: detail.type,
        ...(detail.type === "list" && detail.listItems ? { listItems: detail.listItems } : {}),
      }));
      
      const updateData = {
        details: cleanedDetails,
      };
      await updateDoc(groupRef, updateData);
      
      uiState.setDetailsDialogOpen(false);
      uiState.setEditingGroupDetails([]);
      uiState.setOriginalGroupDetails([]);
      uiState.setEditingGroupName("");
      uiState.setNewDetailName("");
      uiState.setNewDetailType("");
      uiState.setNewDetailDescription("");
      uiState.setNewListItems([]);
      uiState.setDetailNameError("");
      uiState.setEditingDetailIndex(null);
      uiState.setEditingOriginalDetail(null);
    } catch (error) {
      console.error("Error saving details:", error);
      alert(`Failed to save details: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleAddDetailField = (): void => {
    if (!uiState.newDetailName.trim()) {
      uiState.setDetailNameError("Detail name is required");
      return;
    }

    if (!uiState.newDetailType) {
      uiState.setDetailNameError("Detail type is required");
      return;
    }

    if (uiState.editingDetailIndex !== null) {
      // Update existing detail
      const updatedDetails = [...uiState.editingGroupDetails];
      const nameConflict = updatedDetails.some(
        (d, i) => i !== uiState.editingDetailIndex && d.name.toLowerCase() === uiState.newDetailName.trim().toLowerCase()
      );
      if (nameConflict) {
        uiState.setDetailNameError("A detail with this name already exists.");
        return;
      }
      updatedDetails[uiState.editingDetailIndex] = {
        name: uiState.newDetailName.trim(),
        type: uiState.newDetailType,
        ...(uiState.newDetailType === "list" ? { listItems: [...uiState.newListItems] } : {}),
      };
      uiState.setEditingGroupDetails(updatedDetails);
    } else {
      // Add new detail
      if (uiState.editingGroupDetails.some((d) => d.name.toLowerCase() === uiState.newDetailName.trim().toLowerCase())) {
        uiState.setDetailNameError("A detail with this name already exists.");
        return;
      }
      const newDetail: AccountGroupDetailField = {
        name: uiState.newDetailName.trim(),
        type: uiState.newDetailType,
        ...(uiState.newDetailType === "list" ? { listItems: [...uiState.newListItems] } : {}),
      };
      uiState.setEditingGroupDetails([...uiState.editingGroupDetails, newDetail]);
    }
    
    uiState.setNewDetailName("");
    uiState.setNewDetailType("");
    uiState.setNewDetailDescription("");
    uiState.setNewListItems([]);
    uiState.setDetailNameError("");
    uiState.setEditingDetailIndex(null);
    uiState.setEditingOriginalDetail(null);
  };

  const handleRemoveDetailField = (index: number): void => {
    uiState.setEditingGroupDetails(uiState.editingGroupDetails.filter((_, i) => i !== index));
    if (uiState.editingDetailIndex === index) {
      uiState.setEditingDetailIndex(null);
      uiState.setEditingOriginalDetail(null);
    } else if (uiState.editingDetailIndex !== null && uiState.editingDetailIndex > index) {
      uiState.setEditingDetailIndex(uiState.editingDetailIndex - 1);
    }
  };

  const handleEditDetailField = (index: number): void => {
    const detail = uiState.editingGroupDetails[index];
    uiState.setEditingDetailIndex(index);
    uiState.setEditingOriginalDetail({ ...detail });
    uiState.setNewDetailName(detail.name);
    uiState.setNewDetailType(detail.type);
    uiState.setNewListItems(detail.listItems || []);
    uiState.setNewDetailDescription(detail.description || "");
    uiState.setDetailNameError("");
  };

  const handleCancelEditDetail = (): void => {
    uiState.setNewDetailName("");
    uiState.setNewDetailType("");
    uiState.setNewListItems([]);
    uiState.setNewDetailDescription("");
    uiState.setDetailNameError("");
    uiState.setEditingDetailIndex(null);
    uiState.setEditingOriginalDetail(null);
  };

  const handleDetailDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = parseInt(String(active.id));
    const newIndex = parseInt(String(over.id));

    if (isNaN(oldIndex) || isNaN(newIndex)) {
      return;
    }

    const reorderedDetails = arrayMove(uiState.editingGroupDetails, oldIndex, newIndex);
    uiState.setEditingGroupDetails(reorderedDetails);
    
    if (uiState.editingDetailIndex !== null) {
      if (uiState.editingDetailIndex === oldIndex) {
        uiState.setEditingDetailIndex(newIndex);
      } else if (uiState.editingDetailIndex > oldIndex && uiState.editingDetailIndex <= newIndex) {
        uiState.setEditingDetailIndex(uiState.editingDetailIndex - 1);
      } else if (uiState.editingDetailIndex < oldIndex && uiState.editingDetailIndex >= newIndex) {
        uiState.setEditingDetailIndex(uiState.editingDetailIndex + 1);
      }
    }
  };

  const hasEditedDetailChanged = (): boolean => {
    if (uiState.editingDetailIndex === null || !uiState.editingOriginalDetail) {
      return false;
    }

    const original = uiState.editingOriginalDetail;
    const editedName = uiState.newDetailName.trim();
    const editedType = uiState.newDetailType;
    const editedDescription = uiState.newDetailDescription.trim();
    const editedListItems = [...uiState.newListItems].sort();

    if (original.name !== editedName) {
      return true;
    }

    if (original.type !== editedType) {
      return true;
    }

    const originalDescription = (original.description || "").trim();
    if (originalDescription !== editedDescription) {
      return true;
    }

    if (original.type === "list" || editedType === "list") {
      const originalItems = (original.listItems || []).sort();
      if (originalItems.length !== editedListItems.length) {
        return true;
      }
      if (originalItems.join(",") !== editedListItems.join(",")) {
        return true;
      }
    }

    return false;
  };

  // Account handlers - These are simplified versions, full implementations would be very long
  // For now, I'll include the key ones and we can expand as needed
  const handleCreateAccountClick = (): void => {
    uiState.setEditingAccount(null);
    const initialData = {
      accountName: "",
      group: groups.length > 0 ? groups[0].name : "",
      amount: "",
      currency: defaultCurrency,
      description: "",
      details: {},
    };
    uiState.setAccountFormData(initialData);
    uiState.setInitialAccountFormData(null);
    uiState.setShowAccountDetails(false);
    uiState.setInitialShowAccountDetails(false);
    uiState.setEditAccountDialogOpen(true);
  };

  const handleEditAccount = (): void => {
    if (!uiState.selectedAccount) return;
    uiState.setEditingAccount(uiState.selectedAccount);
    const hasDetails = uiState.selectedAccount.details !== undefined;
    const initialData = {
      accountName: uiState.selectedAccount.accountName || "",
      group: uiState.selectedAccount.group || "",
      amount: uiState.selectedAccount.amount?.toString() || "",
      currency: uiState.selectedAccount.currency || "USD",
      description: uiState.selectedAccount.description || "",
      details: uiState.selectedAccount.details || {},
    };
    uiState.setAccountFormData(initialData);
    uiState.setInitialAccountFormData(initialData);
    uiState.setShowAccountDetails(hasDetails);
    uiState.setInitialShowAccountDetails(hasDetails);
    handleAccountMenuClose();
    uiState.setEditAccountDialogOpen(true);
  };

  const handleDuplicateAccount = (): void => {
    if (!uiState.selectedAccount) return;
    uiState.setEditingAccount(null);
    const hasDetails = uiState.selectedAccount.details !== undefined;
    uiState.setAccountFormData({
      accountName: `${uiState.selectedAccount.accountName} (Copy)`,
      group: uiState.selectedAccount.group || "",
      amount: uiState.selectedAccount.amount?.toString() || "",
      currency: uiState.selectedAccount.currency || "USD",
      description: uiState.selectedAccount.description || "",
      details: uiState.selectedAccount.details ? { ...uiState.selectedAccount.details } : {},
    });
    handleAccountMenuClose();
    uiState.setEditAccountDialogOpen(true);
  };

  const handleDeleteAccountClick = (): void => {
    uiState.setAccountMenuAnchor(null);
    uiState.setDeleteAccountDialogOpen(true);
  };

  const handleDeleteAccountConfirm = async (): Promise<void> => {
    // In demo mode, accounts cannot be deleted
    if (isDemoMode()) {
      uiState.setSnackbarElement(
        <DemoModeMessage message="Demo mode: Accounts cannot be deleted. Please log in to delete accounts." />
      );
      uiState.setSnackbarOpen(true);
      uiState.setDeleteAccountDialogOpen(false);
      uiState.setSelectedAccount(null);
      return;
    }

    if (!uiState.selectedAccount || !workspaceId || !auth.currentUser) return;

    try {
      const batch = writeBatch(db);
      const accountRef = doc(db, "workspaces", workspaceId, "accounts", uiState.selectedAccount.id);
      const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
      
      const accountAmount = parseFloat(String(uiState.selectedAccount.amount || 0));
      
      const adjustmentRef = doc(transactionsRef);
      batch.set(adjustmentRef, {
        userId: auth.currentUser.uid,
        type: "adjustment",
        amount: -accountAmount,
        currency: uiState.selectedAccount.currency,
        date: Timestamp.fromDate(new Date()),
        note: accountAmount !== 0 
          ? `Account "${uiState.selectedAccount.accountName}" deleted. Final balance: ${helpers.formatCurrency(accountAmount, uiState.selectedAccount.currency)}`
          : `Account "${uiState.selectedAccount.accountName}" deleted.`,
        accountId: uiState.selectedAccount.id,
        accountName: uiState.selectedAccount.accountName,
        previousAmount: accountAmount,
        isInitialBalance: false,
        createdAt: serverTimestamp(),
      });
      
      batch.delete(accountRef);
      
      await batch.commit();
      
      uiState.setDeleteAccountDialogOpen(false);
      uiState.setSelectedAccount(null);
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete account");
    }
  };

  const handleAccountSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // In demo mode, accounts cannot be created or edited
    if (isDemoMode()) {
      uiState.setSnackbarElement(
        <DemoModeMessage message="Demo mode: Accounts cannot be created or edited. Please log in to manage accounts." />
      );
      uiState.setSnackbarOpen(true);
      uiState.setEditAccountDialogOpen(false);
      uiState.setEditingAccount(null);
      uiState.setAccountFormData({
        accountName: "",
        group: "",
        amount: "",
        currency: "USD",
        description: "",
        details: {},
      });
      uiState.setInitialAccountFormData(null);
      uiState.setShowAccountDetails(false);
      uiState.setAccountNameError("");
      return;
    }
    
    if (!workspaceId || !auth.currentUser) return;
    
    uiState.setIsSavingAccount(true);
    if (!uiState.editingAccount) {
      uiState.setCreatingAccountName(uiState.accountFormData.accountName.trim());
    }

    const amount = uiState.accountFormData.amount === "" || uiState.accountFormData.amount === undefined ? 0 : parseFloat(uiState.accountFormData.amount);
    if (isNaN(amount)) {
      alert("Please enter a valid amount");
      uiState.setIsSavingAccount(false);
      uiState.setCreatingAccountName(null);
      return;
    }

    // Check for duplicate account names (including archived accounts)
    const trimmedName = uiState.accountFormData.accountName.trim();
    const duplicateAccount = accounts.find(
      (acc) => acc.accountName.toLowerCase() === trimmedName.toLowerCase() && 
               acc.id !== uiState.editingAccount?.id
    );

    if (duplicateAccount) {
      const archivedMsg = duplicateAccount.isArchived ? " (archived)" : "";
      uiState.setAccountNameError(`An account named "${duplicateAccount.accountName}"${archivedMsg} already exists. Please use a different name.`);
      uiState.setIsSavingAccount(false);
      uiState.setCreatingAccountName(null);
      return;
    }

    uiState.setAccountNameError("");

    try {
      const accountsInSameGroup = accounts.filter((acc) => acc.group === uiState.accountFormData.group);
      const maxOrder = accountsInSameGroup.length > 0
        ? Math.max(...accountsInSameGroup.map((acc) => (acc as any).order ?? 0))
        : -1;

      const accountData: any = {
        userId: auth.currentUser.uid,
        accountName: uiState.accountFormData.accountName,
        group: uiState.accountFormData.group,
        amount: amount,
        currency: uiState.accountFormData.currency,
        description: uiState.accountFormData.description || "",
        createdAt: uiState.editingAccount
          ? uiState.editingAccount.createdAt
          : serverTimestamp(),
      };

      // Handle details based on checkbox state
      if (uiState.editingAccount) {
        if (uiState.showAccountDetails) {
          accountData.details = uiState.accountFormData.details || {};
        } else {
          accountData.details = deleteField();
        }
      } else {
        if (uiState.showAccountDetails) {
          accountData.details = uiState.accountFormData.details || {};
        }
      }

      if (!uiState.editingAccount) {
        accountData.order = maxOrder + 1;
      }

      if (uiState.editingAccount) {
        const previousAmount = parseFloat(String(uiState.editingAccount.amount || 0));
        const newAmount = amount;
        const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
        const batch = writeBatch(db);
        
        const accountRef = doc(db, "workspaces", workspaceId, "accounts", uiState.editingAccount.id);
        if (uiState.editingAccount.group === uiState.accountFormData.group) {
          accountData.order = (uiState.editingAccount as any).order ?? 0;
        } else {
          const newGroupAccounts = accounts.filter((acc) => acc.group === uiState.accountFormData.group && acc.id !== uiState.editingAccount!.id);
          const maxOrderInNewGroup = newGroupAccounts.length > 0
            ? Math.max(...newGroupAccounts.map((acc) => (acc as any).order ?? 0))
            : -1;
          accountData.order = maxOrderInNewGroup + 1;
        }
        batch.update(accountRef, accountData);
        
        if (previousAmount !== newAmount) {
          const adjustmentAmount = newAmount - previousAmount;
          const adjustmentRef = doc(transactionsRef);
          batch.set(adjustmentRef, {
            userId: auth.currentUser.uid,
            type: "adjustment",
            amount: adjustmentAmount,
            currency: uiState.accountFormData.currency,
            date: Timestamp.fromDate(new Date()),
            note: `Balance adjusted from ${helpers.formatCurrency(previousAmount, uiState.accountFormData.currency)} to ${helpers.formatCurrency(newAmount, uiState.accountFormData.currency)}`,
            accountId: uiState.editingAccount.id,
            accountName: uiState.editingAccount.accountName,
            previousAmount: previousAmount,
            isInitialBalance: false,
            createdAt: serverTimestamp(),
          });
        }
        
        await batch.commit();
      } else {
        const accountsRef = collection(db, "workspaces", workspaceId, "accounts");
        const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
        const batch = writeBatch(db);
        
        const accountRef = doc(accountsRef);
        batch.set(accountRef, accountData);
        
        if (amount !== 0) {
          const adjustmentRef = doc(transactionsRef);
          batch.set(adjustmentRef, {
            userId: auth.currentUser.uid,
            type: "adjustment",
            amount: amount,
            currency: uiState.accountFormData.currency,
            date: Timestamp.fromDate(new Date()),
            note: `Initial balance for account "${uiState.accountFormData.accountName}"`,
            accountId: accountRef.id,
            accountName: uiState.accountFormData.accountName,
            isInitialBalance: true,
            createdAt: serverTimestamp(),
          });
        }
        
        await batch.commit();
      }

      uiState.setEditAccountDialogOpen(false);
      uiState.setEditingAccount(null);
      uiState.setAccountFormData({
        accountName: "",
        group: "",
        amount: "",
        currency: "USD",
        description: "",
        details: {},
      });
      uiState.setInitialAccountFormData(null);
      uiState.setShowAccountDetails(false);
      uiState.setAccountNameError("");
      uiState.setIsSavingAccount(false);
      uiState.setCreatingAccountName(null);
    } catch (error) {
      console.error("Error saving account:", error);
      alert("Failed to save account");
      uiState.setIsSavingAccount(false);
      uiState.setCreatingAccountName(null);
    }
  };

  const handleAccountDragEnd = async (event: DragEndEvent, groupName: string): Promise<void> => {
    // In demo mode, account order cannot be changed
    if (isDemoMode()) {
      uiState.setSnackbarElement(
        <DemoModeMessage message="Demo mode: Account order cannot be changed. Please log in to reorder accounts." />
      );
      uiState.setSnackbarOpen(true);
      return;
    }

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const groupAccounts = helpers.groupedAccounts[groupName] || [];
    const oldIndex = groupAccounts.findIndex((acc) => acc.id === active.id);
    const newIndex = groupAccounts.findIndex((acc) => acc.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedAccounts = arrayMove(groupAccounts, oldIndex, newIndex);
    
    const updatedAccounts = [...accounts];
    reorderedAccounts.forEach((reorderedAccount, index) => {
      const accountIndex = updatedAccounts.findIndex((acc) => acc.id === reorderedAccount.id);
      if (accountIndex !== -1) {
        (updatedAccounts[accountIndex] as any).order = index;
      }
    });
    setAccounts(updatedAccounts);
    
    try {
      const batch = writeBatch(db);
      reorderedAccounts.forEach((account, index) => {
        const accountRef = doc(db, "workspaces", workspaceId, "accounts", account.id);
        batch.update(accountRef, { order: index });
      });
      await batch.commit();
    } catch (error) {
      console.error("Error updating account order:", error);
      setAccounts(accounts);
      alert("Failed to update account order");
    }
  };

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>, account: Account): void => {
    uiState.setAccountMenuAnchor(event.currentTarget);
    uiState.setSelectedAccount(account);
  };

  const handleAccountMenuClose = (): void => {
    uiState.setAccountMenuAnchor(null);
    uiState.setSelectedAccount(null);
  };

  const handleDetailValueChange = (detailName: string, value: string | number): void => {
    uiState.setAccountFormData((prev) => ({
      ...prev,
      details: {
        ...(prev.details || {}),
        [detailName]: value,
      },
    }));
  };

  const toggleAccountDetails = (accountId: string): void => {
    uiState.setExpandedAccountDetails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  return {
    // Group handlers
    handleAddClick,
    handleAddGroup,
    handleEditClick,
    handleUpdateGroup,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDragEnd,
    handleGroupMenuOpen,
    handleGroupMenuClose,
    handleEditGroupFromMenu,
    handleManageDetailsFromMenu,
    handleDeleteGroupFromMenu,
    
    // Detail handlers
    handleSaveDetails,
    handleAddDetailField,
    handleRemoveDetailField,
    handleEditDetailField,
    handleCancelEditDetail,
    handleDetailDragEnd,
    hasEditedDetailChanged,
    
    // Account handlers
    handleCreateAccountClick,
    handleEditAccount,
    handleDuplicateAccount,
    handleDeleteAccountClick,
    handleDeleteAccountConfirm,
    handleAccountSubmit,
    handleAccountDragEnd,
    handleAccountMenuOpen,
    handleAccountMenuClose,
    handleDetailValueChange,
    toggleAccountDetails,
  };
};
